import 'react-circular-progressbar/dist/styles.css'

import { css } from '@emotion/react'
import InfoIcon from 'assets/icons/landing/info.svg?component'
import LightningIcon from 'assets/icons/landing/lightning-icon.svg?component'
import TimeCircleIcon from 'assets/icons/landing/time-circle-icon.svg?component'
import ClockImage from 'assets/images/landing/clock-image.png'
import BodyText from 'components/UI/Landing/body-text'
import Container from 'components/UI/Landing/container'
import SectionTitle from 'components/UI/Landing/section-title'
import Typography from 'components/UI/Typography'
import { useInView } from 'framer-motion'
import { type TFunction } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { buildStyles, CircularProgressbarWithChildren } from 'react-circular-progressbar'
import FlipNumbers from 'react-flip-numbers'
import { useTranslation } from 'react-i18next'
import { Area, CartesianGrid, ComposedChart, ReferenceDot, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { useGetAnalyticsQuery } from 'redux/api/creditApi'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'

import NumberSlider from './statistics-number-slider'

const borderStyle = css`
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    padding: 6px;
    background: linear-gradient(128.93deg, #0dc7ea 0%, #14cb53 97.64%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    transition: opacity 0.3s ease;
  }
`

const data = [
  {
    name: 'min',
    amount: 19_000_000
  },
  {
    name: 'medium',
    amount: 21_835_000
  },
  {
    name: 'max',
    amount: 30_000_000
  }
]

const getFlipNumbers = (count: number | undefined) => {
  if (count === undefined) return '0000'
  const stringCount = count.toString()
  switch (stringCount.length) {
    case 1:
      return `000${stringCount}`
    case 2:
      return `00${stringCount}`
    case 3:
      return `0${stringCount}`
    default:
      return stringCount
  }
}

//mocked values
const getValues = (t: TFunction) => {
  return {
    avarage: {
      money: t('dashboard-section.record-time-card.hours', { value: 24 }),
      contract: t('dashboard-section.record-time-card.hours', { value: 16 }),
      numberContact: 16,
      numberMoney: 24
    },
    record: {
      money: t('dashboard-section.record-time-card.hour', { value: 4 }),
      contract: t('dashboard-section.record-time-card.mins', { value: 32 }),
      numberContact: 0.5,
      numberMoney: 4
    }
  }
}

const Statistics = () => {
  const { t } = useTranslation('landing')

  const [isFetched, setIsFetched] = useState(false)

  const ref = useRef<HTMLDivElement | null>(null)
  const isInView = useInView(ref)

  const { data: analytics, isSuccess } = useGetAnalyticsQuery(null, {
    skipPollingIfUnfocused: true,
    skip: !isInView && !isFetched,
    pollingInterval: isInView ? 90000 : undefined
  })

  const values = useMemo(() => getValues(t), [t])

  const [statisticType, setStatisticType] = useState('avarage')

  const isAvarage = statisticType === 'avarage'

  useEffect(() => {
    if (isSuccess && !isFetched) {
      setIsFetched(true)
    }
  }, [isFetched, isSuccess])

  return (
    <Container ref={ref}>
      <SectionTitle title={t('dashboard-section.title')} />
      <div className='mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-[24px]'>
        <div className='bg-green-gradient  min-h-[316px] rounded-[20px] p-[24px]'>
          <Typography twStyle={tw`text-base lg:text-lg leading-[140%] font-medium text-center  text-white`}>
            {t('dashboard-section.application-accepted-card')}
          </Typography>
          <div className='overflow-hidden gradient-circle relative max-w-[283px] min-h-[283px] rounded-full p-5 mx-auto mt-[28px] lg:mt-[38px] h-[300px]'>
            <NumberSlider state={analytics?.in_progress_count} />
          </div>
        </div>

        <div className='mt-5 lg:mt-0 min-h-[316px] lg:col-span-2 p-4 pb-6 lg:p-6 bg-[var(--bg-landing-card)] rounded-[20px]'>
          <div className={`rounded-[20px] p-2 bg-[var(--bg-landing-toggle)] mb-5 text-main flex gap-2 z-10`}>
            <button
              type='button'
              className={`w-full py-[15px] px-4 text-base leading-[140%] tracking-[-0.01em] font-semibold rounded-xl ${
                isAvarage ? 'bg-[var(--bg-landing-toggle-button)]' : ''
              }`}
              onClick={() => setStatisticType('avarage')}
            >
              {t('dashboard-section.record-time-card.tabs.average-time')}
            </button>
            <button
              type='button'
              className={`w-full py-[15px] px-0 md:px-4 text-base leading-[140%] tracking-[-0.01em] font-semibold rounded-xl ${
                !isAvarage ? 'bg-[var(--bg-landing-toggle-button)]' : ''
              }`}
              onClick={() => setStatisticType('record')}
            >
              {t('dashboard-section.record-time-card.tabs.record-time')}
            </button>
          </div>
          <div className='flex items-center gap-8 lg:gap-[100px] flex-col lg:flex-row '>
            <div className='max-w-[284px]'>
              <CircularProgressbarWithChildren
                value={isAvarage ? values.avarage.numberMoney : values.record.numberMoney}
                maxValue={24}
                styles={buildStyles({
                  pathColor: `#46C8E0`,
                  trailColor: `var(--bg-landing-toggle)`,
                  backgroundColor: `var(--bg-landing-toggle)`
                })}
              >
                <div className='max-w-[230px]'>
                  <CircularProgressbarWithChildren
                    value={isAvarage ? values.avarage.numberContact : values.record.numberContact}
                    maxValue={24}
                    styles={buildStyles({
                      pathColor: `#52D878`,
                      trailColor: `var(--bg-landing-toggle)`,
                      backgroundColor: `var(--bg-landing-toggle)`
                    })}
                  >
                    {isAvarage ? (
                      <TimeCircleIcon tw='text-[#EAECED] dark:text-[#2B2B2E]' />
                    ) : (
                      <LightningIcon tw='text-[#EAECED] dark:text-[#2B2B2E]' />
                    )}
                  </CircularProgressbarWithChildren>
                </div>
              </CircularProgressbarWithChildren>
            </div>
            <div className='flex flex-col gap-6 lg:gap-8 max-w-[240px] text-center lg:text-left'>
              <div className='flex flex-col gap-2'>
                <Typography twStyle={tw`font-medium text-base lg:text-lg leading-[140%]`}>
                  {t('dashboard-section.record-time-card.money-recieved-time-text')}
                </Typography>
                <Typography twStyle={tw`font-bold text-2xl lg:text-[32px] leading-[140%] text-[#46C8E0]`}>
                  {isAvarage ? values.avarage.money : values.record.money}
                </Typography>
              </div>
              <div className='flex flex-col gap-2'>
                <Typography twStyle={tw`font-medium text-base lg:text-lg leading-[140%]`}>
                  {t('dashboard-section.record-time-card.docs-sign-time-text')}
                </Typography>
                <Typography twStyle={tw`font-bold  text-2xl lg:text-[32px] leading-[140%] text-[#52D878]`}>
                  {isAvarage ? values.avarage.contract : values.record.contract}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <div className='min-h-[316px] grey-gradient flex flex-col bg-[var(--bg-landing-card)] overflow-hidden rounded-[20px] p-6 px-4 lg:px-6 relative'>
          <p className='text-lg leading-[140%] text-[#C7C7CC]'>{t('dashboard-section.applications-per-hour')}</p>
          <p className='font-bold text-[32px] leading-[140%] mt-2 mb-[14px]'>
            {numberWithSpaces(analytics?.application_per_hour)}
          </p>
          <div className='bg-circle-green-gradient w-[175px] h-[175px] absolute bottom-0 right-0'></div>
          <img
            alt='clock'
            src={ClockImage}
            className='w-[200px] lg:w-[175px] h-[200px] lg:h-[175px] absolute bottom-0 right-0'
          />
        </div>
        <div className='min-h-[316px] bg-[var(--bg-landing-card)] rounded-[20px] relative overflow-hidden'>
          <div className='absolute'>
            <p className='px-6 pt-6 mb-2 text-lg leading-[140%] text-[#C7C7CC]'>
              {t('dashboard-section.average-loan-amount')}
            </p>
            <p className='px-6 font-bold text-[32px] leading-[140%] '>21 835 000 ₸</p>
          </div>
          <ResponsiveContainer width='100%' height={323}>
            <ComposedChart data={data} margin={{ bottom: 0, right: 0 }}>
              <defs>
                <linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='10%' stopColor='rgba(70, 200, 224, 0.25)' stopOpacity={0.3} />
                  <stop offset='90%' stopColor='rgba(82, 216, 120, 0.25)' stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id='dotGradient' x1='0' y1='0' x2='0' y2='1' gradientTransform='rotate(-45)'>
                  <stop offset='25%' stopColor='rgba(70, 200, 224, 1)' />
                  <stop offset='75%' stopColor='rgba(82, 216, 120, 1)' />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray='1 4' />
              <XAxis dataKey='name' hide />
              <YAxis
                unit={'M'}
                domain={['dataMin', 'dataMax']}
                range={[19, 20, 21]}
                interval={'preserveEnd'}
                mirror
                ticks={[19_000_000, 20_000_000, 21_000_000, 22_000_000, 23_000_000, 24_000_000, 25_000_000, 26_000_000]}
                includeHidden
                tickFormatter={(val: number) => {
                  return (val / 1_000_000).toString()
                }}
              />
              <ReferenceDot
                r={10}
                stroke='#FFFFFF'
                strokeWidth={3}
                fill='url(#dotGradient)'
                x={'medium'}
                y={21_835_000}
              />
              <Area type='monotone' dataKey='amount' stroke='none' fill='url(#colorUv)' />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className='min-h-[316px] flex flex-col bg-[var(--bg-landing-card)] rounded-[20px] p-6 px-4 lg:px-6'>
          <p className='text-lg leading-[140%] text-[#C7C7CC]'>
            {t('dashboard-section.applications-per-day-card.title')}
          </p>
          <div css={[tw`rounded-[20px] mt-6 relative p-4`, borderStyle]} className='counter-container'>
            <FlipNumbers
              play
              color='var(--stats-counter-text)'
              background='var(--stats-counter-bg)'
              width={37}
              height={60}
              numbers={getFlipNumbers(analytics?.today_in_progress_count)}
              duration={2}
            />
          </div>
          <div
            className={`mt-[46px] flex items-center gap-3 px-0 md:px-3 py-3 rounded-lg text-muted sm:bg-[var(--bg-landing-warning)] bg-none`}
          >
            <div className='shrink-0'>
              <InfoIcon />
            </div>
            <BodyText
              text={t('dashboard-section.applications-per-day-card.real-time-text')}
              className='text-sm leading-[140%] font-medium text-[#AEAEB2] dark'
            />
          </div>
        </div>
      </div>
    </Container>
  )
}

export default Statistics
